import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { Button } from '../button/Button';

interface Props {
  children: ReactNode;
  type?: 'success' | 'error' | 'warning' | 'info';
  className?: string;
  size?: 'small' | 'medium';
  closeable?: boolean;
  onClose?: () => void;
}

export function Alert(props: Props) {
  const {
    children,
    type = 'info',
    className,
    size = 'medium',
    closeable = false,
    onClose,
  } = props;
  let Icon;

  switch (type) {
    case 'success':
      Icon = CheckCircleIcon;
      break;
    case 'error':
      Icon = XCircleIcon;
      break;
    case 'warning':
      Icon = ExclamationTriangleIcon;
      break;
    default:
      Icon = InformationCircleIcon;
  }

  return (
    <div
      className={clsx(
        'flex items-center rounded-md',
        size === 'small' && 'p-2',
        size === 'medium' && 'p-4',
        type === 'success' && 'bg-green-50 dark:bg-green-900',
        type === 'error' && 'bg-red-50 dark:bg-red-900',
        type === 'warning' && 'bg-yellow-50 dark:bg-yellow-900',
        type === 'info' && 'bg-blue-50 dark:bg-blue-900',
        ...(className || '')
      )}
    >
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon
            className={clsx(
              'h-5 w-5',
              size === 'small' && 'h-4 w-4',
              type === 'success' && 'text-green-400 dark:text-green-200',
              type === 'error' && 'text-red-400 dark:text-red-100',
              type === 'warning' && 'text-yellow-400 dark:text-yellow-200',
              type === 'info' && 'text-blue-400 dark:text-blue-200'
            )}
            aria-hidden="true"
          />
        </div>
        <div className="ml-3 flex-1 md:flex md:justify-between">
          <p
            className={clsx(
              'text-sm',
              size === 'small' && 'text-xs',
              type === 'success' && 'text-green-700 dark:text-green-200',
              type === 'error' && 'text-red-700 dark:text-red-100',
              type === 'warning' && 'text-yellow-700 dark:text-yellow-200',
              type === 'info' && 'text-blue-700 dark:text-blue-200'
            )}
          >
            {children}
          </p>
        </div>
      </div>
      {closeable && (
        <div className="ml-auto">
          <Button
            variant="icon"
            onClick={() => {
              onClose && onClose();
            }}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default Alert;
